import React, { useEffect, useRef, useState } from 'react';
import { startCheckout } from '../services/billingService';
import { AppStep, GarageProfile } from '../types';

interface LeftPanelProps {
  currentStep: AppStep;
  isProcessing: boolean;
  onUpload: (file: File, type: 'carteGrise' | 'identity', role?: 'buyer' | 'seller') => void;
  onNext: () => void;
  onBack: () => void;
  onDownload: () => void;
  onSaveDossier?: () => void;
  onValidate: () => void;
  isValidated: boolean;
  cerfaDirty?: boolean;
  garageProfile: GarageProfile | null;
  onSaveGarage: (profile: GarageProfile) => void;
  isPaying: boolean;
  activeDossierId?: number | null;
}

const createEmptyGarage = (): GarageProfile => ({
  name: '',
  siret: '',
  address: '',
  postalCode: '',
  city: '',
  logoData: ''
});

const LeftPanel: React.FC<LeftPanelProps> = ({
  currentStep,
  isProcessing,
  onUpload,
  onNext, 
  onBack,
  onDownload,
  onSaveDossier,
  onValidate,
  isValidated,
  cerfaDirty,
  garageProfile,
  onSaveGarage,
  isPaying,
  activeDossierId
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<{ type: 'carteGrise' | 'identity'; role?: 'buyer' | 'seller' } | null>(null);
  const [showGarageSettings, setShowGarageSettings] = useState(false);
  const [tempGarage, setTempGarage] = useState<GarageProfile>(garageProfile || createEmptyGarage());
  const [identityRole, setIdentityRole] = useState<'buyer' | 'seller'>('buyer');
  const [logoError, setLogoError] = useState<string | null>(null);

  // Sync the temporary form with the stored garage profile whenever the modal opens
  useEffect(() => {
    if (showGarageSettings) {
      setTempGarage(garageProfile ?? createEmptyGarage());
    }
  }, [garageProfile, showGarageSettings]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && uploadType) {
      onUpload(e.target.files[0], uploadType.type, uploadType.role);
    }
  };

  const triggerUpload = (type: 'carteGrise' | 'identity', role?: 'buyer' | 'seller') => {
    setUploadType({ type, role });
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }
  };

  const handleSaveGarage = () => {
    setLogoError(null);
    onSaveGarage(tempGarage);
    setShowGarageSettings(false);
  };

  const handleLogoUpload = (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("Le logo doit être une image");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setTempGarage((prev) => ({ ...prev, logoData: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="h-full flex flex-col p-4 md:p-8 text-white relative overflow-hidden bg-slate-900">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 z-0"></div>
      
      <div className="relative z-10 flex flex-col h-full overflow-y-auto no-scrollbar">
        <header className="mb-4 flex justify-between items-start shrink-0">
          <div>
            <div className="flex items-center space-x-2 md:space-x-3 mb-1">
              <span className="material-symbols-outlined text-2xl md:text-4xl text-blue-400">document_scanner</span>
              <h1 className="text-lg md:text-2xl font-bold tracking-tight">AutoCerfa AI</h1>
            </div>
            <p className="text-slate-400 text-[10px] md:text-sm">Vente de Véhicule (Garage)</p>
          </div>
          <button 
            onClick={() => setShowGarageSettings(!showGarageSettings)}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors relative"
            title="Paramètres Garage"
          >
            <span className="material-symbols-outlined text-slate-300">settings</span>
            {!garageProfile?.name && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>}
          </button>
        </header>

        {showGarageSettings && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 md:absolute md:inset-auto md:top-16 md:left-4 md:right-4 md:p-0">
                <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 animate-fade-in shadow-xl w-full max-w-sm md:max-w-none">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm md:text-lg font-bold flex items-center">
                          <span className="material-symbols-outlined mr-2">garage</span>
                          Profil Garage (Vendeur)
                      </h3>
                      <button onClick={() => setShowGarageSettings(false)} className="md:hidden">
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                    <div className="space-y-2 md:space-y-3">
                        <div>
                            <label className="text-[10px] md:text-xs text-slate-400">Nom du Garage / Société</label>
                            <input type="text" className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm" 
                                value={tempGarage.name} onChange={e => setTempGarage({...tempGarage, name: e.target.value})} placeholder="Ex: Auto Occasion 3000" />
                        </div>
                        <div>
                            <label className="text-[10px] md:text-xs text-slate-400">SIRET</label>
                            <input type="text" className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm" 
                                value={tempGarage.siret} onChange={e => setTempGarage({...tempGarage, siret: e.target.value})} />
                        </div>
                        <div>
                            <label className="text-[10px] md:text-xs text-slate-400">Adresse</label>
                            <input type="text" className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm" 
                                value={tempGarage.address} onChange={e => setTempGarage({...tempGarage, address: e.target.value})} />
                        </div>
                        <div className="flex gap-2">
                            <div className="w-1/3">
                                <label className="text-[10px] md:text-xs text-slate-400">CP</label>
                                <input type="text" className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm" 
                                    value={tempGarage.postalCode} onChange={e => setTempGarage({...tempGarage, postalCode: e.target.value})} />
                            </div>
                            <div className="w-2/3">
                                <label className="text-[10px] md:text-xs text-slate-400">Ville</label>
                                <input type="text" className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm" 
                                    value={tempGarage.city} onChange={e => setTempGarage({...tempGarage, city: e.target.value})} />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] md:text-xs text-slate-400">Logo du garage (affiché sur la facture)</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                                    className="text-[11px] md:text-xs"
                                />
                                {tempGarage.logoData && (
                                    <img src={tempGarage.logoData} alt="Logo garage" className="h-10 w-10 object-contain border border-slate-600 rounded bg-white" />
                                )}
                            </div>
                            {logoError && <div className="text-xs text-red-400 mt-1">{logoError}</div>}
                        </div>
                        <div className="flex gap-2 mt-2">
                           <button onClick={() => setShowGarageSettings(false)} className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 rounded font-bold text-xs md:text-sm md:hidden">
                                Annuler
                            </button>
                            <button onClick={handleSaveGarage} className="flex-1 py-2 bg-green-600 hover:bg-green-500 rounded font-bold text-xs md:text-sm">
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Warning if no garage profile */}
        {!garageProfile?.name && !showGarageSettings && (
             <div className="bg-yellow-600/20 border border-yellow-600/50 p-2 rounded mb-4 text-[10px] md:text-xs text-yellow-200 flex items-center cursor-pointer shrink-0" onClick={() => setShowGarageSettings(true)}>
                <span className="material-symbols-outlined text-sm md:text-base mr-2">info</span>
                Configurez votre profil garage.
             </div>
        )}

        <div className="flex-1 flex flex-col overflow-y-auto pr-1">
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={handleFileChange}
            />

            {/* Steps Visualization */}
            <div className="space-y-4 md:space-y-6 flex-1">
                
                {/* Step 1: Carte Grise */}
                <div className={`transition-all duration-500 ${currentStep === AppStep.UPLOAD_CARTE_GRISE || currentStep === AppStep.WELCOME ? 'opacity-100 transform translate-x-0' : 'opacity-40 hidden md:block'}`}>
                    <div className="flex items-center mb-2">
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold mr-3 text-sm md:text-base ${currentStep === AppStep.UPLOAD_CARTE_GRISE ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>1</div>
                        <h2 className="text-base md:text-xl font-semibold">Le Véhicule</h2>
                    </div>
                    {currentStep === AppStep.UPLOAD_CARTE_GRISE && (
                        <div className="ml-9 md:ml-11">
                            <p className="text-slate-400 mb-3 text-xs md:text-sm">Scanner la Carte Grise.</p>
                            <button 
                                onClick={() => triggerUpload('carteGrise')}
                                disabled={isProcessing}
                                className="w-full py-3 md:py-4 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed group mb-2 text-sm md:text-base"
                            >
                                {isProcessing ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                        <span className="">Analyse IA (Serveur)...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined group-hover:-translate-y-1 transition-transform">upload_file</span>
                                        <span className="">Scanner Carte Grise</span>
                                    </>
                                )}
                            </button>
                            <button onClick={onNext} className="text-xs text-slate-500 underline hover:text-white w-full text-center py-2">Passer cette étape</button>
                        </div>
                    )}
                </div>

                {/* Step 2: Info Acheteur */}
                <div className={`transition-all duration-500 ${currentStep === AppStep.UPLOAD_ID ? 'opacity-100 transform translate-x-0' : 'opacity-40 hidden md:block'}`}>
                    <div className="flex items-center mb-2">
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold mr-3 text-sm md:text-base ${currentStep === AppStep.UPLOAD_ID ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>2</div>
                        <h2 className="text-base md:text-xl font-semibold">L'Acheteur</h2>
                    </div>
                    {currentStep === AppStep.UPLOAD_ID && (
                         <div className="ml-9 md:ml-11">
                             <p className="text-slate-400 mb-3 text-xs md:text-sm">Scanner la pièce d'identité.</p>
                            <div className="space-y-3">
                                <div className="flex bg-slate-800 rounded-lg p-1 text-[11px] md:text-xs border border-slate-700">
                                    <button
                                        className={`flex-1 py-2 rounded-md transition-colors ${identityRole === 'buyer' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                                        onClick={() => setIdentityRole('buyer')}
                                        disabled={isProcessing}
                                    >
                                        Acheteur
                                    </button>
                                    <button
                                        className={`flex-1 py-2 rounded-md transition-colors ${identityRole === 'seller' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                                        onClick={() => setIdentityRole('seller')}
                                        disabled={isProcessing}
                                    >
                                        Vendeur
                                    </button>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => triggerUpload('identity', identityRole)}
                                        disabled={isProcessing}
                                        className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-xs md:text-sm flex items-center justify-center space-x-1 transition-all disabled:opacity-50"
                                    >
                                        {isProcessing ? <span className="material-symbols-outlined animate-spin text-base">progress_activity</span> : <span className="material-symbols-outlined text-base">id_card</span>}
                                        <span>{identityRole === 'seller' ? "Scanner ID Vendeur" : "Scan ID Client"}</span>
                                    </button>
                                </div>
                                <div className="flex justify-between pt-2">
                                    <button onClick={onBack} className="text-slate-400 hover:text-white text-xs flex items-center px-2 py-1 rounded hover:bg-slate-800">
                                        <span className="material-symbols-outlined text-sm mr-1">arrow_back</span>
                                        Retour
                                    </button>
                                    <button onClick={onNext} className="text-slate-400 hover:text-white text-xs flex items-center px-2 py-1 rounded hover:bg-slate-800">
                                        Passer
                                        <span className="material-symbols-outlined text-sm ml-1">arrow_forward</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 3: Review & Validation */}
                <div className={`transition-all duration-500 ${currentStep === AppStep.REVIEW ? 'opacity-100 transform translate-x-0' : 'opacity-40 hidden md:block'}`}>
                    <div className="flex items-center mb-2">
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold mr-3 text-sm md:text-base ${currentStep === AppStep.REVIEW ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-400'}`}>3</div>
                        <h2 className="text-base md:text-xl font-semibold">Validation</h2>
                    </div>
                    {currentStep === AppStep.REVIEW && (
                        <div className="ml-9 md:ml-11">
                            <p className="text-slate-300 mb-4 text-xs md:text-sm">
                                {isValidated 
                                    ? "Document prêt. Vous pouvez télécharger." 
                                    : "Vérifiez et validez les informations."
                                }
                            </p>
                            
                            {!isValidated ? (
                                <button 
                                    onClick={onValidate}
                                    className="w-full py-3 md:py-4 bg-blue-600 hover:bg-blue-500 rounded-lg font-bold flex items-center justify-center space-x-2 transition-all mb-3 text-sm md:text-base"
                                >
                                    <span className="material-symbols-outlined">check_circle</span>
                                    <span>Valider et Sauvegarder</span>
                                </button>
                            ) : (
                                <div className="mb-3 text-center bg-green-500/20 text-green-300 p-2 rounded border border-green-500/50 text-xs">
                                    <span className="material-symbols-outlined text-sm align-middle mr-1">verified</span>
                                    Modifications enregistrées
                                </div>
                            )}

                            <button 
                                onClick={onDownload}
                                disabled={isProcessing || !isValidated}
                                className={`w-full py-3 md:py-4 rounded-lg font-bold shadow-lg flex items-center justify-center space-x-2 transition-all text-sm md:text-base ${
                                    isValidated && !isProcessing
                                    ? 'bg-green-600 hover:bg-green-500 hover:scale-105 shadow-green-900/20 text-white' 
                                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                }`}
                            >
                                {isProcessing ? (
                                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                ) : (
                                    <span className="material-symbols-outlined">download</span>
                                )}
                                <span>Télécharger le CERFA</span>
                            </button>
                            {activeDossierId && (
                              <button
                                onClick={onSaveDossier}
                                disabled={(!isValidated && !cerfaDirty) || isProcessing}
                                className={`mt-3 w-full py-3 md:py-4 rounded-lg font-bold flex items-center justify-center space-x-2 transition-all text-sm md:text-base ${
                                  ((isValidated || cerfaDirty) && !isProcessing)
                                    ? 'bg-sky-600 hover:bg-sky-500 hover:scale-105 text-white'
                                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                }`}
                              >
                                {isProcessing ? (
                                  <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                ) : (
                                  <span className="material-symbols-outlined">task_alt</span>
                                )}
                                <span>{cerfaDirty ? "Enregistrer les modifications" : "Enregistrer dans le dossier"}</span>
                              </button>
                            )}
                             <button onClick={onBack} className="mt-4 text-slate-400 hover:text-white text-xs flex items-center">
                                <span className="material-symbols-outlined text-sm mr-1">arrow_back</span>
                                Retour aux étapes
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>

        <div className="mt-6 p-4 rounded-xl border border-slate-700 bg-slate-800/60">
            <div className="text-sm font-semibold text-slate-100">Fonctionnalités incluses</div>
            <ul className="mt-2 text-[12px] text-slate-300 space-y-1 list-disc list-inside">
                <li>Scan CG / ID / justificatif + annotation</li>
                <li>Pré-remplissage Cerfa et dossiers regroupés</li>
                <li>Factures PDF ajoutées au dossier</li>
                <li>Archivage et export ZIP des dossiers</li>
                <li>Stockage extensible et suivi d’espace</li>
            </ul>
        </div>

        {!isPaying && (
          <div className="mt-3 p-4 rounded-xl border border-amber-400/60 bg-amber-500/10">
              <div className="text-sm font-semibold text-amber-200">Passer en Pro – 19,90 €/mois</div>
              <p className="text-[12px] text-amber-100/90 mt-1">
                  Factures, dossiers, stockage étendu et support prioritaire pour les pros de l’auto.
              </p>
              <button
                  onClick={() => startCheckout()}
                  className="mt-3 w-full px-3 py-2 rounded-md bg-amber-400 text-slate-900 font-semibold text-sm hover:bg-amber-300 transition"
              >
                  Activer l’abonnement Pro
              </button>
          </div>
        )}

        <footer className="mt-2 md:pt-4 border-t border-slate-700 text-center text-[10px] text-slate-500 shrink-0">
            <p className="font-semibold text-slate-400">AutoCerfa par T-LiveMedia</p>
            <p className="mt-0.5 text-slate-500">Assistant carte grise et dossiers pros</p>
        </footer>
      </div>
    </div>
  );
};

export default LeftPanel;
